import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import PublicAuthActions from "./PublicAuthActions";
import { C, pill } from "./MarketplaceShell";
import { SITE_WRAP } from "./siteStyles";

/**
 * The header and footer the redesigned public site shares — Home and Find a business today.
 *
 * One component rather than a copy per page, because the nav is a list of promises ("this is where
 * Pricing is") and two copies of it would eventually disagree about where that is.
 */

export type SitePage = "home" | "find" | "send" | "contact";

/** Every destination is a real route; nothing here points at "#". */
/**
 * `hint` explains a label whose name doesn't say what it does: the product is called "Send my
 * Business", but the bar says the outcome. The desktop bar is one fixed-height line, so the hint
 * shows only in the mobile menu and as the link's tooltip.
 */
const NAV_LINKS: { key: SitePage; label: string; to: string; hint?: string }[] = [
  { key: "home", label: "Home", to: "/" },
  { key: "find", label: "Find a business", to: "/find" },
  { key: "send", label: "Advertise to DA applicants", to: "/send-my-business", hint: "Send my Business — your business in the packs we post to development applicants" },
  { key: "contact", label: "Contact", to: "/contact" },
];

const FOOTER_LINKS: { label: string; to: string }[] = [
  { label: "About", to: "/about" },
  { label: "Trust & Safety", to: "/trust" },
  { label: "Contact", to: "/contact" },
  { label: "Privacy", to: "/privacy" },
];

export function SiteHeader({ active }: { active: SitePage }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();
  // Closed again on any navigation, so arriving on a page never shows last page's open menu.
  useEffect(() => setMenuOpen(false), [pathname]);

  return (
    <header style={{ background: "#fff", borderBottom: `1px solid ${C.line}`, position: "sticky", top: 0, zIndex: 20 }}>
      <div style={{ ...SITE_WRAP, display: "flex", alignItems: "center", gap: 22, padding: "12px 28px" }}>
        <Link to="/" style={{ display: "block", flex: "none" }}>
          <img src="/images/partner-network/logo.png" alt="DAconnect" style={{ height: 40, width: "auto", display: "block" }} />
        </Link>
        {/* The design's own overflow rule: the links wrap onto a second line that the fixed height
            clips, so a narrow screen loses the last links rather than breaking the bar. */}
        <nav
          className="site-nav"
          aria-label="Main"
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 4,
            height: 35,
            overflow: "hidden",
            fontSize: 14,
            fontWeight: 600,
            whiteSpace: "nowrap",
            minWidth: 0,
            flex: 1,
          }}
        >
          {NAV_LINKS.map((link) => {
            const on = link.key === active;
            return (
              <Link
                key={link.key}
                to={link.to}
                aria-current={on ? "page" : undefined}
                title={link.hint}
                style={{
                  padding: "8px 11px",
                  borderRadius: 99,
                  background: on ? C.tint : "transparent",
                  color: on ? C.deep : "#3d423c",
                }}
              >
                {link.label}
              </Link>
            );
          })}
        </nav>
        <PublicAuthActions
          gap={18}
          className="site-auth"
          style={{ marginLeft: "auto", whiteSpace: "nowrap", flex: "none" }}
          linkStyle={{ fontSize: 14, fontWeight: 600, color: "#3d423c" }}
          pillStyle={{ ...pill, fontSize: 14, background: C.deep, color: "#fff", padding: "10px 22px" }}
          pillClassName="site-pill"
        />
        <button
          type="button"
          className="site-burger"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          aria-controls="site-menu"
          onClick={() => setMenuOpen((open) => !open)}
          style={{
            marginLeft: "auto",
            width: 42,
            height: 42,
            placeItems: "center",
            borderRadius: 10,
            border: `1px solid ${C.cardLine}`,
            background: "#fff",
            color: C.ink,
            cursor: "pointer",
            flex: "none",
          }}
        >
          <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            {menuOpen ? <path d="M6 6l12 12M18 6 6 18" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
          </svg>
        </button>
      </div>

      {menuOpen && (
        <div
          id="site-menu"
          className="site-menu"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            background: "#fff",
            borderBottom: `1px solid ${C.line}`,
            boxShadow: "0 16px 30px -18px rgba(20,33,20,.3)",
            padding: "8px 20px 20px",
          }}
        >
          <nav aria-label="Menu" style={{ display: "flex", flexDirection: "column" }}>
            {NAV_LINKS.map((link) => {
              const on = link.key === active;
              return (
                <Link
                  key={link.key}
                  to={link.to}
                  aria-current={on ? "page" : undefined}
                  onClick={() => setMenuOpen(false)}
                  style={{
                    padding: "13px 10px",
                    borderRadius: 10,
                    fontSize: 16,
                    fontWeight: 600,
                    color: on ? C.deep : C.ink,
                    background: on ? C.tint : "transparent",
                  }}
                >
                  {link.label}
                  {link.hint && (
                    <span style={{ display: "block", fontSize: 13, fontWeight: 500, color: C.bodyMuted, marginTop: 2 }}>{link.hint}</span>
                  )}
                </Link>
              );
            })}
          </nav>
          <PublicAuthActions
            gap={10}
            style={{ marginTop: 14 }}
            onNavigate={() => setMenuOpen(false)}
            linkStyle={{ flex: 1, textAlign: "center", fontSize: 15, fontWeight: 700, color: C.ink, padding: "12px 16px", borderRadius: 99, border: `1px solid ${C.cardLine}` }}
            pillStyle={{ ...pill, flex: 1, textAlign: "center", fontSize: 15, background: C.deep, color: "#fff", padding: "12px 16px" }}
            pillClassName="site-pill"
          />
        </div>
      )}
    </header>
  );
}

/**
 * The dark green closing strip — three promises and "Contact us" — and the legal line under it.
 * Each page brings its own three promises; both pages' are claims the rest of the site already makes.
 */
export function SiteFooter({ trust }: { trust: { title: string; body: string }[] }) {
  return (
    <footer style={{ background: C.deep, color: "#fff", marginTop: "auto" }}>
      <div
        style={{
          ...SITE_WRAP,
          padding: "22px 28px",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))",
          gap: "18px 30px",
          alignItems: "center",
        }}
      >
        {trust.map((t) => (
          <div key={t.title} style={{ borderLeft: "2px solid rgba(134,239,172,.45)", paddingLeft: 14 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.mint }}>{t.title}</div>
            <div style={{ fontSize: 12.5, color: C.mintInk, marginTop: 2 }}>{t.body}</div>
          </div>
        ))}
        <Link
          to="/contact"
          className="site-pill"
          style={{
            justifySelf: "end",
            background: C.mint,
            color: C.deep,
            borderRadius: 99,
            fontSize: 14,
            fontWeight: 700,
            padding: "12px 24px",
          }}
        >
          Contact us →
        </Link>
      </div>
      <div style={{ borderTop: "1px solid rgba(134,239,172,.2)" }}>
        <div
          style={{
            ...SITE_WRAP,
            padding: "14px 28px",
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
            fontSize: 12,
            color: C.mintFaint,
          }}
        >
          <span>© 2026 DAconnect Pty Ltd</span>
          <span style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
            {FOOTER_LINKS.map((link) => (
              <Link key={link.label} to={link.to} style={{ color: "inherit" }}>{link.label}</Link>
            ))}
          </span>
        </div>
      </div>
    </footer>
  );
}
